/**
 * Video detail page Mixpanel event constants and helpers.
 */

import { Dict, RequestOptions } from 'mixpanel-browser';

export const VideoDetailEvents = {
    /** The "Visit Hub" section was rendered/scrolled into view on the video player page. */
    HUB_COMPONENT_IMPRESSION: 'HubComponentImpression',
    /** The user clicked/tapped "Visit Hub" from the video player page. */
    HUB_VIEW_CLICK: 'HubViewClick',
} as const;
export type VideoDetailEventName = (typeof VideoDetailEvents)[keyof typeof VideoDetailEvents];

/** Properties shared by both "Visit Hub" component events. */
interface HubEventProperties {
    /** The qualifying channel's slug. */
    channel_id: string | null;
}

interface VideoDetailEventPropertiesMap {
    [VideoDetailEvents.HUB_COMPONENT_IMPRESSION]: HubEventProperties;
    [VideoDetailEvents.HUB_VIEW_CLICK]: HubEventProperties;
}

type EventProperties<E extends VideoDetailEventName> = E extends keyof VideoDetailEventPropertiesMap
    ? VideoDetailEventPropertiesMap[E] & Dict
    : Dict;

/**
 * HubViewClick precedes navigation to the hub, so it must be sent via navigator.sendBeacon —
 * otherwise the in-flight XHR is cancelled the moment the browser starts unloading the page and
 * the event is lost. `send_immediately: true` alone is not enough (see search-events.ts).
 */
const EVENTS_NEEDING_BEACON: ReadonlySet<VideoDetailEventName> = new Set([VideoDetailEvents.HUB_VIEW_CLICK]);

export const trackVideoDetailEvent = <E extends VideoDetailEventName>(eventName: E, properties: EventProperties<E>) => {
    const options: RequestOptions | undefined = EVENTS_NEEDING_BEACON.has(eventName)
        ? { transport: 'sendBeacon', send_immediately: true }
        : undefined;
    window.vumedi?.mixpanel?.track(eventName, properties, options);
};

/**
 * Fire once when the "Visit Hub" section is rendered. The hub is two breakpoint-specific
 * components (desktop `vm-video-hub-related`, mobile `vm-video-hub-card`) that both live in
 * the DOM, so each can call this independently — this module-level guard ensures only the
 * first call (the surface the user actually sees) emits the event per page load.
 */
let hubImpressionFired = false;
export const trackHubComponentImpression = (channelId: string | null) => {
    if (hubImpressionFired) return;
    hubImpressionFired = true;
    trackVideoDetailEvent(VideoDetailEvents.HUB_COMPONENT_IMPRESSION, { channel_id: channelId });
};

/** Reset the once-per-page impression guard. Exported for tests. */
export const resetHubImpressionTracking = () => {
    hubImpressionFired = false;
};

/** Fire when the user clicks/taps "Visit Hub". */
export const trackHubViewClick = (channelId: string | null) => {
    trackVideoDetailEvent(VideoDetailEvents.HUB_VIEW_CLICK, { channel_id: channelId });
};
